import React, { useCallback, useMemo, useState } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import ReportIcon from '@mui/icons-material/Report';
import BlockIcon from '@mui/icons-material/Block';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import { useIsAdmin } from '../../../hooks/auth/useIsAdmin';
import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel } from '@mui/material';

const formatDuration = (millis) => {
  if (!millis || millis <= 0) return null;
  const totalSeconds = Math.floor(millis / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const paddedMinutes = String(minutes).padStart(2, '0');
  const paddedSeconds = String(seconds).padStart(2, '0');
  if (hours > 0) {
    return `${hours}:${paddedMinutes}:${paddedSeconds}`;
  }
  return `${paddedMinutes}:${paddedSeconds}`;
};

const formatCreationDateTime = (millis) => {
  if (!millis) return null;
  const date = new Date(millis);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = date.getDate();
  const month = months[date.getMonth()];
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return {
    date: `${day} ${month}`,
    time: `${hours}:${minutes}`,
  };
};

const overlayBaseStyles = {
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  color: 'white',
  pointerEvents: 'auto',
  zIndex: 2,
  height: (theme) => theme.spacing(3),
  boxSizing: 'border-box',
};

const MediaCard = (props) => {
  const {
    mediaPoint,
    onClick,
    isSelected,
    isFavorite,
    onToggleFavorite,
    onToggleLike,
    onShareClick,
    onDeleteClick,
    onReportClick,
    onBlockClick,
    onBlockUserClick
  } = props;

  const { keycloak } = useKeycloak();
  const isAdmin = useIsAdmin();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const isMenuOpen = Boolean(anchorEl);

  const [blockUserDialogOpen, setBlockUserDialogOpen] = useState(false);
  const [blockVideos, setBlockVideos] = useState(true);

  const isOwner = useMemo(() => {
    return keycloak.authenticated && mediaPoint.ownerId === keycloak.tokenParsed?.sub;
  }, [keycloak.authenticated, keycloak.tokenParsed, mediaPoint.ownerId]);

  const { imageUrl, isLive, statusText, formattedDuration, creationDateTime } = useMemo(() => {
    const isLive = mediaPoint.status === 'STREAM';
    let duration = null;
    if (!isLive && mediaPoint.finishedTimestamp && mediaPoint.startedTimestamp) {
      const durationMillis = mediaPoint.finishedTimestamp - mediaPoint.startedTimestamp;
      duration = formatDuration(durationMillis);
    }
    const creationDateTime = formatCreationDateTime(mediaPoint.startedTimestamp);
    return {
      imageUrl: mediaPoint.thumbnailUrl || '/video_thumbnail.jpg',
      isLive,
      statusText: isLive ? 'LIVE' : (mediaPoint.status === 'BLOCKED' ? 'BLOCKED' : 'VIDEO'),
      formattedDuration: duration,
      creationDateTime,
    };
  }, [mediaPoint]);

  const stopEvent = useCallback((event) => {
    event.stopPropagation();
    event.preventDefault();
  }, []);

  const handleFavoriteClick = useCallback((event) => {
    stopEvent(event);
    onToggleFavorite(mediaPoint);
  }, [mediaPoint, onToggleFavorite, stopEvent]);

  const handleLikeClick = useCallback((event) => {
    stopEvent(event);
    onToggleLike(mediaPoint);
  }, [mediaPoint, onToggleLike, stopEvent]);

  const handleMenuOpen = useCallback((event) => {
    stopEvent(event);
    setAnchorEl(event.currentTarget);
  }, [stopEvent]);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleDeleteMenuItemClick = useCallback((event) => {
    handleMenuClose();
    stopEvent(event);
    if (window.confirm('Are you sure you want to delete this media?')) {
      onDeleteClick(mediaPoint);
    }
  }, [handleMenuClose, onDeleteClick, mediaPoint, stopEvent]);

  const handleReportMenuItemClick = useCallback((event) => {
    handleMenuClose();
    stopEvent(event);
    const reason = window.prompt("Please state the reason for your report:");
    if (reason) {
      onReportClick(mediaPoint, reason);
    }
  }, [handleMenuClose, onReportClick, mediaPoint, stopEvent]);

  const handleBlockMenuItemClick = useCallback((event) => {
    handleMenuClose();
    stopEvent(event);
    const action = mediaPoint.status === 'BLOCKED' ? 'unblock' : 'block';
    if (window.confirm(`Are you sure you want to ${action} this media?`)) {
      onBlockClick(mediaPoint);
    }
  }, [handleMenuClose, onBlockClick, mediaPoint, stopEvent]);

  const handleBlockUserMenuItemClick = useCallback((event) => {
    handleMenuClose();
    stopEvent(event);
    setBlockUserDialogOpen(true);
  }, [handleMenuClose, stopEvent]);

  const handleConfirmBlockUser = () => {
    onBlockUserClick(mediaPoint.ownerId, blockVideos);
    setBlockUserDialogOpen(false);
  };


  return (
    <>
      <Card
        sx={{
          width: '100%',
          height: 'auto',
          cursor: 'pointer',
          border: isSelected ? '2px solid #2196F3' : '1px solid transparent',
          boxShadow: isSelected ? '0 4px 20px rgba(33, 150, 243, 0.3)' : 1,
          borderRadius: (theme) => theme.spacing(2),
          overflow: 'hidden',
          opacity: mediaPoint.status === 'BLOCKED' ? 0.5 : 1,
        }}
        onClick={onClick}
      >
        <Box sx={{ position: 'relative' }}>
          <CardMedia
            sx={{
              aspectRatio: '9/16',
              width: '100%',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundColor: 'grey.200',
            }}
            image={imageUrl}
            title={mediaPoint.title || 'Media thumbnail'}
          />

          <Box
            sx={{
              ...overlayBaseStyles,
              top: (t) => t.spacing(1),
              left: (t) => t.spacing(1),
              width: (t) => t.spacing(3),
              borderRadius: '50%',
              justifyContent: 'center',
              transition: 'background-color 0.2s ease-in-out',
              ...(isFavorite && {
                bgcolor: 'error.main',
              }),
            }}
            onClick={handleFavoriteClick}
          >
            <IconButton aria-label="add to favorites" size="small" sx={{ p: 0, color: 'white' }}>
              {isFavorite ? (
                <FavoriteIcon sx={{ fontSize: '1rem', color: 'white' }} />
              ) : (
                <FavoriteBorderIcon sx={{ fontSize: '1rem' }} />
              )}
            </IconButton>
          </Box>

          <Box
            sx={{
              ...overlayBaseStyles,
              top: (t) => t.spacing(1),
              right: (t) => t.spacing(1),
              borderRadius: '12px',
              px: 1,
              ...(isLive && {
                bgcolor: 'error.main',
                boxShadow: (t) => `0px 2px 8px ${t.palette.error.dark}`,
              }),
              ...(mediaPoint.status === 'BLOCKED' && {
                bgcolor: 'text.secondary',
              }),
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 'bold', lineHeight: 1 }}>
              {statusText}
            </Typography>
          </Box>

          {creationDateTime && (
            <Box
              sx={{
                ...overlayBaseStyles,
                top: (t) => t.spacing(1),
                left: '50%',
                transform: 'translateX(-50%)',
                borderRadius: '8px',
                flexDirection: 'column',
                justifyContent: 'center',
                height: 'auto',
                p: 0.5
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 'bold', lineHeight: 1.1 }}>
                {creationDateTime.date}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.65rem', lineHeight: 1.1 }}>
                {creationDateTime.time}
              </Typography>
            </Box>
          )}

          {formattedDuration && (
            <Box sx={{ ...overlayBaseStyles, bottom: (t) => t.spacing(1), right: (t) => t.spacing(1), borderRadius: '12px', px: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                {formattedDuration}
              </Typography>
            </Box>
          )}

          <Box
            sx={{ ...overlayBaseStyles, bottom: (t) => t.spacing(1), left: (t) => t.spacing(1), borderRadius: '12px', pl: 1, pr: 0.5 }}
            onClick={(e) => e.stopPropagation()}
          >
            {mediaPoint.likesCount > 0 && (
              <Typography variant="caption" sx={{ fontWeight: 'bold', mr: 0.5 }}>
                {mediaPoint.likesCount}
              </Typography>
            )}
            <IconButton
              size="small"
              aria-label="toggle like"
              onClick={handleLikeClick}
              sx={{ color: 'white', ...(mediaPoint.likedByCurrentUser && { color: 'primary.light' }) }}
            >
              {mediaPoint.likedByCurrentUser ? (
                <ThumbUpIcon sx={{ fontSize: '0.875rem' }} />
              ) : (
                <ThumbUpOffAltIcon sx={{ fontSize: '0.875rem' }} />
              )}
            </IconButton>
          </Box>
        </Box>

        <CardContent sx={{ pt: 1, pb: '8px !important', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ minWidth: 0, pr: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
              {mediaPoint.ownerLogin}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap title={mediaPoint.description}>
              {mediaPoint.description || ' '}
            </Typography>
          </Box>

          {keycloak.authenticated && (
            <Box>
              <IconButton
                aria-label="more"
                id="media-card-menu-button"
                aria-controls={isMenuOpen ? 'media-card-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={isMenuOpen ? 'true' : undefined}
                onClick={handleMenuOpen}
                size="small"
                sx={{ mr: -1 }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
              <Menu
                id="media-card-menu"
                anchorEl={anchorEl}
                open={isMenuOpen}
                onClose={handleMenuClose}
                MenuListProps={{ 'aria-labelledby': 'media-card-menu-button' }}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    overflow: 'visible',
                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                    mt: 1.5,
                    '& .MuiAvatar-root': {
                      width: 32,
                      height: 32,
                      ml: -0.5,
                      mr: 1,
                    },
                    '&:before': {
                      content: '""',
                      display: 'block',
                      position: 'absolute',
                      top: 0,
                      right: 14,
                      width: 10,
                      height: 10,
                      bgcolor: 'background.paper',
                      transform: 'translateY(-50%) rotate(45deg)',
                      zIndex: 0,
                    },
                  },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                {!isOwner && (
                  <MenuItem onClick={handleReportMenuItemClick}>
                    <ListItemIcon>
                      <ReportIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Report</ListItemText>
                  </MenuItem>
                )}

                {isAdmin && (
                  <MenuItem onClick={handleBlockMenuItemClick}>
                    <ListItemIcon>
                      <BlockIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>
                      {mediaPoint.status === 'BLOCKED' ? 'Unblock Media' : 'Block Media'}
                    </ListItemText>
                  </MenuItem>
                )}

                {isAdmin && !isOwner && (
                  <MenuItem onClick={handleBlockUserMenuItemClick}>
                    <ListItemIcon>
                      <PersonOffIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Block User</ListItemText>
                  </MenuItem>
                )}


                {isOwner && (
                  <MenuItem onClick={handleDeleteMenuItemClick}>
                    <ListItemIcon>
                      <DeleteIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Delete</ListItemText>
                  </MenuItem>
                )}

              </Menu>
            </Box>
          )}
        </CardContent>
      </Card>
      <Dialog open={blockUserDialogOpen} onClose={() => setBlockUserDialogOpen(false)}>
        <DialogTitle>Block User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to block the user "{mediaPoint.ownerLogin}"? This will prevent them from logging in.
          </DialogContentText>
          <FormControlLabel
            control={
              <Checkbox
                checked={blockVideos}
                onChange={(e) => setBlockVideos(e.target.checked)}
                name="blockVideosCheckbox"
                color="primary"
              />
            }
            label="Block all videos by this user"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockUserDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmBlockUser} color="error">
            Block User
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MediaCard;